/**
 * Ticketing-service inter-service contracts consumed by kiosk-service.
 * Shapes mirror the implemented API, not aspirational admin/web schemas.
 */

import { CustomerIdSchema } from '@autopia/shared-types';
import { z } from 'zod';

// Deep import from the leaf module (not the ../kiosk barrel) to avoid a circular
// import: the barrel re-exports wristband-identity.ts, which imports
// KioskAttractionTicketSchema back from this file. See kiosk/payment-method.ts.
import { KioskPaymentMethodSchema } from '../kiosk/payment-method';

// Same leaf-module reason as above: ./attraction-attachment imports nothing back.
import { OrderAttractionAttachmentSchema } from './attraction-attachment';

export const TicketingServiceEnvelopeSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    success: z.boolean(),
    data: dataSchema,
  });

// ============================================================================
// POST /kiosk/purchase  (walk-up order creation)
// ============================================================================

export const CreateKioskOrderItemSchema = z.object({
  ticketTypeCode: z.string().min(1),
  quantity: z.number().int().positive(),
  visitorName: z.string().optional(),
  slotId: z.string().optional(),
  slotStartTime: z.string().optional(),
  slotEndTime: z.string().optional(),
});

export type CreateKioskOrderItem = z.infer<typeof CreateKioskOrderItemSchema>;

export const CreateKioskOrderBundleItemSchema = z.object({
  ticketTypeCode: z.string().min(1),
  category: z.string().min(1),
  quantity: z.number().int().positive(),
});

export const CreateKioskOrderBundleSchema = z.object({
  bundleId: z.string().uuid(),
  bundleCode: z.string().min(1),
  quantity: z.number().int().positive(),
  bundlePriceIdr: z.number().int().nonnegative(),
  items: z.array(CreateKioskOrderBundleItemSchema).min(1),
});

export const CreateKioskOrderRequestSchema = z.object({
  kioskId: z.string().min(1),
  /**
   * The kiosk terminal's identity-service `User.id`, so ticketing can attribute
   * the order to a real identity instead of synthesising one from a device id.
   *
   * `kioskId` above is a `Kiosk.id` — a device, not a principal — which is why
   * ticketing used to manufacture `KIOSK_WALKUP_<kioskId>` for the owner column.
   * kiosk-service already holds the real value: every walk-up route resolves its
   * terminal with `kiosk.findUnique({ where: { userId } })`
   * (`kiosk-resolver.service.ts:15`), so a kiosk that can reach this endpoint
   * necessarily has one to send.
   *
   * **Required since #856 — this field is where the Parallel Change contracts.**
   * It was `.optional()` for exactly as long as that was load-bearing:
   * `api-contracts` is a `workspace:*` dependency of 13 services and 4 apps, each
   * running the schema version it last built, so additive-and-optional was the
   * only shape safe at every intermediate deploy state while #847 (kiosk-service
   * sends it) and #848 (ticketing prefers it) landed in either order. Both are
   * deployed and `kiosk-purchase.service.ts` no longer has a fallback, so the
   * option now only permits a walk-up order with no real owner — a sentinel for
   * #852 to clean up.
   *
   * ⚠️ **Narrowing costs forward compatibility, and that is the whole risk of
   * this change.** Removing an optional field means producers must upgrade
   * first; a kiosk-service build predating #847 now gets a 400 at the point of
   * sale. It fails loud rather than silent, which is the good version of this
   * failure, but it fails where a visitor is standing.
   *
   * ⚠️ **Rolling this line back is NOT sufficient to restore the sale.** It was
   * committed alone so the *contract* can be reverted alone, which is not the
   * same thing. `kiosk-purchase.service.ts` also throws a `BadRequestException`
   * on an absent `kioskUserId`, and that guard shipped in the commit before this
   * one, deliberately, so no commit in the branch could let an absent owner
   * reach a `NOT NULL` column. Restoring `.optional()` alone just moves the 400
   * from the pipe to the service. **To make an owner-less walk-up sale succeed
   * again you must revert both commits** — the contract narrowing and the
   * fallback removal — and at that point you are back to writing sentinels.
   *
   * Derives from `CustomerIdSchema` rather than re-stating a shape (Rule #7c), so
   * a sentinel cannot enter here. Rejection is safe at this boundary: a
   * `ZodValidationPipe` answers 400, unlike a Kafka consumer's `safeParse`, which
   * skips the message in silence (ADR-037 decision 4, Sequencing).
   */
  kioskUserId: CustomerIdSchema,
  kioskTransactionId: z.string().min(1),
  visitDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  items: z.array(CreateKioskOrderItemSchema),
  bundles: z.array(CreateKioskOrderBundleSchema).optional(),
  /**
   * ATTRACTION selections, carrying the parent instance each one was attached
   * to. They belong here rather than in `items[]`: an item row loses the
   * parent mapping the fulfilment saga needs to set `Ticket.parentTicketId`,
   * and an ATTRACTION item without it aborts fulfilment (#862). Sending the
   * same attraction in both places would bill and mint it twice.
   */
  attractionAttachments: z.array(OrderAttractionAttachmentSchema).optional(),
  totalIdr: z.number().int().positive(),
  paymentMethod: KioskPaymentMethodSchema,
  customerName: z.string().min(1),
  customerEmail: z.string().email(),
});

export type CreateKioskOrderRequest = z.infer<typeof CreateKioskOrderRequestSchema>;

export const CreateKioskOrderResponseDataSchema = z.object({
  orderId: z.string().min(1),
  orderNumber: z.string().min(1),
  totalIdr: z.number().int(),
  status: z.string(),
  expiresAt: z.string().nullable(),
  itemCount: z.number().int().nonnegative(),
  ticketIds: z.array(z.string()),
});

export type CreateKioskOrderResponseData = z.infer<typeof CreateKioskOrderResponseDataSchema>;

export const CreateKioskOrderResponseSchema = TicketingServiceEnvelopeSchema(
  CreateKioskOrderResponseDataSchema,
);

export type CreateKioskOrderResponse = z.infer<typeof CreateKioskOrderResponseSchema>;

// ============================================================================
// Shared item + response-data schemas (used by initiate and fulfill below)
// ============================================================================

export const RecordKioskAddonOrderItemSchema = z.object({
  attractionId: z.string().min(1),
  attractionCode: z.string().min(1),
  ticketTypeCode: z.string().min(1),
  slotId: z.string().min(1),
  slotDate: z.string().min(1),
  slotStartTime: z.string().min(1),
  slotEndTime: z.string().optional(),
  unitPriceIdr: z.number().int().nonnegative(),
});

export const RecordKioskAddonOrderResponseDataSchema = z.object({
  orderId: z.string().min(1),
  orderNumber: z.string().min(1),
  items: z.array(
    z.object({
      attractionId: z.string(),
      ticketId: z.string(),
    }),
  ),
});

export type RecordKioskAddonOrderResponseData = z.infer<
  typeof RecordKioskAddonOrderResponseDataSchema
>;

// ============================================================================
// POST /kiosk/addon-order/initiate  (pre-payment order reservation)
// ============================================================================

export const InitiateKioskAddonOrderRequestSchema = z.object({
  kioskTransactionId: z.string().min(1),
  kioskId: z.string().min(1),
  wristbandCode: z.string().min(1),
  visitDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  totalIdr: z.number().int().nonnegative(),
  items: z.array(RecordKioskAddonOrderItemSchema).min(1),
});

export type InitiateKioskAddonOrderRequest = z.infer<typeof InitiateKioskAddonOrderRequestSchema>;

export const InitiateKioskAddonOrderResponseDataSchema = z.object({
  orderId: z.string().min(1),
  orderNumber: z.string().min(1),
});

export type InitiateKioskAddonOrderResponseData = z.infer<
  typeof InitiateKioskAddonOrderResponseDataSchema
>;

export const InitiateKioskAddonOrderResponseSchema = TicketingServiceEnvelopeSchema(
  InitiateKioskAddonOrderResponseDataSchema,
);

export type InitiateKioskAddonOrderResponse = z.infer<typeof InitiateKioskAddonOrderResponseSchema>;

// ============================================================================
// POST /kiosk/addon-order/fulfill  (post-payment ticket generation)
// ============================================================================

export const FulfillKioskAddonOrderRequestSchema = z.object({
  orderId: z.string().min(1),
  kioskTransactionId: z.string().min(1),
  paymentMethod: KioskPaymentMethodSchema,
  paidAt: z.string().min(1),
  paymentId: z.string().min(1),
  gateway: z.string().min(1),
});

export type FulfillKioskAddonOrderRequest = z.infer<typeof FulfillKioskAddonOrderRequestSchema>;

export const FulfillKioskAddonOrderResponseSchema = TicketingServiceEnvelopeSchema(
  RecordKioskAddonOrderResponseDataSchema,
);

export type FulfillKioskAddonOrderResponse = z.infer<typeof FulfillKioskAddonOrderResponseSchema>;

// ============================================================================
// GET /tickets/by-wristband/:code/attractions
// ============================================================================

export const KioskAttractionTicketSchema = z.object({
  id: z.string().min(1),
  ticketNumber: z.string(),
  attractionId: z.string(),
  attractionCode: z.string(),
  attractionName: z.string().nullable(),
  ticketTypeCode: z.string(),
  slotId: z.string(),
  slotDate: z.string().nullable().optional(),
  slotStartTime: z.string().nullable().optional(),
  slotEndTime: z.string().nullable().optional(),
  expiresAt: z.string(),
  usedAt: z.string().nullable(),
  createdAt: z.string(),
  visitDate: z.string().optional(),
  status: z.enum(['USED', 'EXPIRED', 'ACTIVE']),
});

export type KioskAttractionTicket = z.infer<typeof KioskAttractionTicketSchema>;

export const KioskAttractionTicketsResponseSchema = TicketingServiceEnvelopeSchema(
  z.array(KioskAttractionTicketSchema),
);

// ============================================================================
// GET /tickets?qrCode=  +  GET /orders/:ref  (pickup resolution)
// ============================================================================

export const TicketQrLookupSchema = z
  .object({
    id: z.string().optional(),
    ticketId: z.string().optional(),
    orderNumber: z.string().optional(),
    order: z
      .object({
        orderNumber: z.string().optional(),
      })
      .optional(),
  })
  .passthrough();

export type TicketQrLookup = z.infer<typeof TicketQrLookupSchema>;

export const KioskPickupOrderTicketSchema = z
  .object({
    id: z.string().optional(),
    ticketId: z.string().optional(),
    ticketNumber: z.string().optional(),
    ticketTypeName: z.string().optional(),
    ticketTypeCode: z.string().optional(),
    category: z.string().optional(),
    visitorName: z.string().nullable().optional(),
    attractionCode: z.string().optional(),
    attraction: z
      .object({
        code: z.string().optional(),
        name: z.string().optional(),
      })
      .optional(),
    ticketType: z
      .object({
        name: z.string().optional(),
      })
      .optional(),
    slotStartTime: z.string().nullable().optional(),
    slotEndTime: z.string().nullable().optional(),
    slot: z
      .object({
        startTime: z.string().nullable().optional(),
        endTime: z.string().nullable().optional(),
      })
      .optional(),
  })
  .passthrough();

export const KioskPickupOrderItemSchema = z
  .object({
    ticketTypeCode: z.string().optional(),
    ticketTypeName: z.string().optional(),
    category: z.string().optional(),
    visitorName: z.string().nullable().optional(),
    visitorEmail: z.string().nullable().optional(),
  })
  .passthrough();

export const KioskPickupOrderDetailSchema = z
  .object({
    id: z.string().optional(),
    orderId: z.string().optional(),
    orderNumber: z.string(),
    status: z.string(),
    visitDate: z.string(),
    customerName: z.string().nullable().optional(),
    customerEmail: z.string().nullable().optional(),
    customer: z
      .object({
        name: z.string().optional(),
        email: z.string().optional(),
      })
      .optional(),
    groupOrder: z.unknown().optional(),
    groupCoordinatorId: z.string().nullable().optional(),
    items: z.array(KioskPickupOrderItemSchema).optional(),
    tickets: z.array(KioskPickupOrderTicketSchema).optional(),
  })
  .passthrough();

export type KioskPickupOrderDetail = z.infer<typeof KioskPickupOrderDetailSchema>;

export const KioskPickupOrderResponseSchema = TicketingServiceEnvelopeSchema(
  KioskPickupOrderDetailSchema,
);

// ============================================================================
// GET /kiosk/purchase/:orderId  (walk-up order poll)
// ============================================================================

export const KioskPurchaseOrderTicketSchema = z
  .object({
    id: z.string().min(1),
    category: z.string().optional(),
    qrCodeToken: z.string().optional(),
    visitorName: z.string().nullable().optional(),
    ticketType: z
      .object({
        name: z.string().optional(),
      })
      .nullable()
      .optional(),
  })
  .passthrough();

export const KioskPurchaseOrderDetailSchema = z
  .object({
    id: z.string().optional(),
    orderNumber: z.string().optional(),
    /**
     * The owner ticketing-service actually recorded for this order.
     *
     * Deliberately `z.string()` and **not** `CustomerIdSchema`: this reads a
     * value back rather than accepting one.
     *
     * ⚠️ This comment used to promise the schema would tighten "once #856
     * removes the fallback". **That was wrong, and #856 deliberately left it
     * loose**, for two reasons rather than one:
     *
     *   1. #856 stops the sentinel being *written* on the walk-up path; it does
     *      not change what is already stored. A wristband issued today against
     *      an order created before `2026-08-08T04:17:12Z` still reads back
     *      `KIOSK_WALKUP_<kioskId>`.
     *   2. New rows can still carry it. `initiateAddonOrder`
     *      (`kiosk-purchase.service.ts`) inherits its owner from the parent
     *      entry ticket, so an addon bought against a pre-cutover walk-up
     *      creates a fresh Order and Ticket with the sentinel — after #856, and
     *      with no cutover timestamp to bound it.
     *
     * A strict schema here would reject the service's own output on a live
     * kiosk path. It tightens when #852's backfill has removed the last such
     * row, not before.
     *
     * kiosk-service reads it so a wristband is stamped with the same owner the
     * order carries, whichever of #847 and #848 deployed first. Inferring it
     * independently is what let the two formulas drift in the first place.
     *
     * Already present in the response — `OrdersService.serializeOrder` spreads
     * the row — so declaring it here is a type-level correction, not a new
     * field on the wire.
     */
    customerId: z.string().optional(),
    customerName: z.string().nullable().optional(),
    visitDate: z.string().optional(),
    tickets: z.array(KioskPurchaseOrderTicketSchema).optional(),
  })
  .passthrough();

export type KioskPurchaseOrderDetail = z.infer<typeof KioskPurchaseOrderDetailSchema>;
